import { defineActions } from '@noven-prime/lattice-core';
import { defaultSiteContent, DEFAULT_SITE_MODE, withContentDefaults, } from '@denise-augusta/domain';
import { getSiteContentStore } from './store.js';
/**
 * Site Content capability actions
 */
export const siteContentActions = defineActions({
    /**
     * Get the currently published content (falls back to baked-in defaults).
     */
    getContent: {
        id: 'getContent',
        name: 'Get Site Content',
        description: 'Get the published site content document, or the defaults if none published',
        params: { type: 'object', properties: {} },
        result: { type: 'object' },
        access: {
            scopes: ['root', 'admin', 'user'],
        },
        execute: async (_params, _context) => {
            const published = await getSiteContentStore().load();
            return withContentDefaults(published);
        },
    },
    /**
     * Get the working draft, falling back to the published document (so the
     * editor always has something to start from).
     */
    getDraft: {
        id: 'getDraft',
        name: 'Get Draft',
        description: 'Get the working draft, or the published content if no draft exists',
        params: { type: 'object', properties: {} },
        result: { type: 'object' },
        access: {
            scopes: ['root', 'admin'],
        },
        execute: async (_params, _context) => {
            const store = getSiteContentStore();
            const draft = await store.loadDraft();
            if (draft)
                return { content: withContentDefaults(draft), isDraft: true };
            const published = await store.load();
            return { content: withContentDefaults(published), isDraft: false };
        },
    },
    /**
     * Save the working draft. Never touches the published site.
     */
    saveDraft: {
        id: 'saveDraft',
        name: 'Save Draft',
        description: 'Persist the working draft — the published site is unchanged',
        params: {
            type: 'object',
            properties: {
                content: { type: 'object', description: 'The full SiteContent draft document' },
            },
            required: ['content'],
        },
        result: { type: 'object' },
        access: {
            scopes: ['root', 'admin'],
        },
        execute: async (params, _context) => {
            const next = {
                ...params.content,
                updatedAt: new Date().toISOString(),
            };
            await getSiteContentStore().saveDraft(next);
            return next;
        },
    },
    /**
     * Discard the working draft.
     */
    discardDraft: {
        id: 'discardDraft',
        name: 'Discard Draft',
        description: 'Delete the working draft — the published site is unchanged',
        params: { type: 'object', properties: {} },
        result: { type: 'object' },
        access: {
            scopes: ['root', 'admin'],
        },
        execute: async (_params, _context) => {
            await getSiteContentStore().deleteDraft();
            return { discarded: true };
        },
    },
    /**
     * Promote the working draft to the live published document. Bumps the
     * published version and removes the draft.
     */
    promoteDraft: {
        id: 'promoteDraft',
        name: 'Promote Draft to Live',
        description: 'Make the working draft the live published site content',
        params: { type: 'object', properties: {} },
        result: { type: 'object' },
        access: {
            scopes: ['root', 'admin'],
        },
        execute: async (_params, _context) => {
            const store = getSiteContentStore();
            const draft = await store.loadDraft();
            if (!draft) {
                throw new Error('No draft to promote');
            }
            const published = await store.load();
            const next = {
                ...draft,
                version: (published?.version ?? defaultSiteContent.version) + 1,
                updatedAt: new Date().toISOString(),
            };
            await store.save(next);
            await store.deleteDraft();
            return next;
        },
    },
    /**
     * Published/draft status for the dashboard.
     */
    getContentStatus: {
        id: 'getContentStatus',
        name: 'Get Content Status',
        description: 'Published version and whether a working draft exists',
        params: { type: 'object', properties: {} },
        result: { type: 'object' },
        access: {
            scopes: ['root', 'admin'],
        },
        execute: async (_params, _context) => {
            const store = getSiteContentStore();
            const [published, draft] = await Promise.all([store.load(), store.loadDraft()]);
            const effective = published ?? defaultSiteContent;
            return {
                publishedVersion: effective.version,
                publishedAt: effective.updatedAt,
                hasDraft: draft !== null,
                draftUpdatedAt: draft?.updatedAt ?? null,
            };
        },
    },
    /**
     * Get the site access mode (locked by default).
     */
    getSiteMode: {
        id: 'getSiteMode',
        name: 'Get Site Mode',
        description: 'Get the current site access mode — locked (sign-in required) or live',
        params: { type: 'object', properties: {} },
        result: { type: 'object' },
        access: {
            scopes: ['root', 'admin', 'user'],
        },
        execute: async (_params, _context) => {
            const mode = await getSiteContentStore().loadMode();
            return { mode: mode ?? DEFAULT_SITE_MODE };
        },
    },
    /**
     * Set the site access mode.
     */
    setSiteMode: {
        id: 'setSiteMode',
        name: 'Set Site Mode',
        description: 'Switch the public site between locked (sign-in required) and live (open)',
        params: {
            type: 'object',
            properties: {
                mode: {
                    type: 'string',
                    enum: ['locked', 'live'],
                    description: 'locked = sign-in required to view the site; live = open to everyone',
                },
            },
            required: ['mode'],
        },
        result: { type: 'object' },
        access: {
            scopes: ['root', 'admin'],
        },
        execute: async (params, _context) => {
            await getSiteContentStore().saveMode(params.mode);
            return { mode: params.mode };
        },
    },
});
//# sourceMappingURL=actions.js.map